import {
  Modal as ManModal,
  Box as ManBox,
  TextInput as MuiTextInput,
  Group as ManGroup,
  Button as ManButton,
  Select as ManSelect,
} from "@mantine/core"
import { useEffect, useRef, useState } from "react"

import ErrorText from "../../components/ErrorText"
import { axiosInstance, axiosInstanceNoAuth, axiosReq } from "../../api/axiosDefaults"
import { Controller, useWatch } from "react-hook-form"
import { CLIENT_PROGRAM_HOSTNAME } from "../../utils/config"
import useHostName from "../../hooks/useHostName"
import callnames from "../../data/callnames"
import toast from "react-hot-toast"

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.error ||
  error?.response?.data?.detail ||
  error?.response?.data?.message ||
  error?.message ||
  fallback

const AddUserModal = ({
  handleCloseAddUserModal,
  opened,
  handleSubmit,
  register,
  errors,
  isSubmitting,
  onSubmit,
  control,
  setValue,
  userType
}) => {
  const host = useHostName()
  const [allGroups, setAllGroups] = useState([])
  const [characters, setCharacters] = useState([])

  const selectedGroup = useWatch({
    control,
    name: "groups",
  })

  const selectedCharacter = useWatch({
    control,
    name: "character",
  })
  const prevSelectedGroupRef = useRef()

  // useEffect(() => {
  //   axiosInstanceNoAuth
  //     .get("/groups/")
  //     .then((res) => {
  //       let groupResults = res.data.results;
  //       // If user is Admin, filter out Superadmin
  //       if (userType === "Admin") {
  //         groupResults = groupResults.filter((group) => group.name !== "Superadmin");
  //       }
  //       const updatedGrpArray = groupResults.map((r) => ({
  //         label: r.name,
  //         value: r.id,
  //       }));
  //       setAllGroups(updatedGrpArray);
  //     })
  //     .catch((err) => {
  //       console.log(err);
  //     });
  // }, []);
  useEffect(() => {
    if (!userType) return
    axiosInstanceNoAuth
      .get("/groups/")
      .then((res) => {
        let groupResults = res.data.results

        if (userType === "Admin") {
          groupResults = groupResults.filter((group) => group.name !== "Superadmin")
        }

        const updatedGrpArray = groupResults.map((r) => ({
          label: r.name,
          value: r.id,
        }))

        setAllGroups(updatedGrpArray)
      })
      .catch((err) => {
        console.error("Error fetching groups:", err)
      })
  }, [userType])

  useEffect(() => {
    const fetchCharacters = async () => {
      try {
        let res
        if (host === CLIENT_PROGRAM_HOSTNAME) {
          res = await axiosReq.get("/characters/")
        } else {
          res = await axiosInstance.get(
            `${localStorage.getItem("projectSlug")}/characters/`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
              },
              withCredentials: true,
            }
          )
        }
        if (res?.success === false || res?.status === false) {
          throw res?.error || new Error("Failed to fetch characters")
        }
        const updatedCharacterArray = (res.data?.results || []).map((character) => ({
          label: character.role,
          value: character.id,
          callTimeUserName: character.username || "",
        }))
        setCharacters(updatedCharacterArray)
      } catch (err) {
        console.error("Error fetching characters:", err)
        toast.error(getErrorMessage(err, "Failed to fetch characters"))
      }
    }

    fetchCharacters()
  }, [host])

  const selectedRoleName = allGroups.find(
    (group) => String(group.value) === String(selectedGroup)
  )?.label
  const isCastRole = (selectedRoleName || "").toLowerCase() === "cast"
  const isCrewRole = (selectedRoleName || "").toLowerCase() === "crew"
  const characterOptions = [
    { label: "\u00A0", value: "" },
    ...characters.map(({ label, value }) => ({ label, value })),
  ]
  const callnameOptions = [
    { label: "\u00A0", value: "" },
    ...callnames.map((name) => ({ label: name, value: name })),
  ]

  useEffect(() => {
    if (prevSelectedGroupRef.current === undefined) {
      prevSelectedGroupRef.current = selectedGroup
      return
    }

    if (prevSelectedGroupRef.current === selectedGroup) return

    prevSelectedGroupRef.current = selectedGroup
    setValue("character", null, {
      shouldDirty: false,
      shouldValidate: false,
    })
    setValue("callTimeUserName", "", {
      shouldDirty: false,
      shouldValidate: false,
    })
  }, [selectedGroup, setValue])

  useEffect(() => {
    if (isCastRole) return
    setValue("character", null)
    setValue("callTimeUserName", "", {
      shouldDirty: false,
      shouldValidate: false,
    })
  }, [isCastRole, setValue])

  useEffect(() => {
    if (!isCastRole || !selectedCharacter) return

    const selectedCharacterData = characters.find(
      (character) => String(character.value) === String(selectedCharacter)
    )

    if (!selectedCharacterData) return

    setValue("callTimeUserName", selectedCharacterData.callTimeUserName, {
      shouldDirty: true,
      shouldValidate: false,
    })
  }, [characters, isCastRole, selectedCharacter, setValue])

  return (
    <ManModal
      opened={opened}
      onClose={handleCloseAddUserModal}
      title="Add User"
      centered
    >
      <ManBox maw={400} mx="auto">
        <form onSubmit={handleSubmit(onSubmit)}>
          <MuiTextInput
            name="email"
            withAsterisk
            label="Email"
            placeholder="Enter email"
            {...register("email")}
          />
          {errors.email && <ErrorText message={errors.email?.message} />}

          <MuiTextInput
            name="username"
            withAsterisk
            label="Username"
            placeholder="Enter username"
            {...register("username")}
          />
          {errors.username && <ErrorText message={errors.username?.message} />}

          <MuiTextInput
            name="firstName"
            withAsterisk
            label="First Name"
            placeholder="Enter First Name"
            {...register("firstName")}
          />
          {errors.firstName && (
            <ErrorText message={errors.firstName?.message} />
          )}

          <MuiTextInput
            name="lastName"
            withAsterisk
            label="Last Name"
            placeholder="Enter Last Name"
            {...register("lastName")}
          />
          {errors.lastName && <ErrorText message={errors.lastName?.message} />}

          <MuiTextInput
            name="phoneNumber"
            withAsterisk
            label="Phone Number"
            placeholder="Enter Phone Number"
            {...register("phoneNumber")}
          />
          {errors.phoneNumber && (
            <ErrorText message={errors.phoneNumber?.message} />
          )}

          <Controller
            name="groups"
            control={control}
            render={({ field }) => (
              <ManSelect

                data={allGroups}
                value={field.value}
                onChange={(value) => field.onChange(value)}
                label="Role"
                withAsterisk
                // error={!!errors.grpups}
                // errorLabel={errors.groups && errors.groups.message}
                placeholder="Please select a role"
              />
            )}
          />

          {errors.groups && <ErrorText message={errors.groups?.message} />}

          {isCastRole && (
            <Controller
              name="character"
              control={control}
              render={({ field }) => (
                <ManSelect
                  data={characterOptions}
                  value={field.value}
                  onChange={(value) => field.onChange(value === "" ? null : value)}
                  label="Character"
                  placeholder="Please select a character"
                  searchable
                  nothingFound="No characters found"
                />
              )}
            />
          )}

          {isCrewRole ? (
            <Controller
              name="callTimeUserName"
              control={control}
              render={({ field }) => (
                <ManSelect
                  data={callnameOptions}
                  value={field.value ?? null}
                  onChange={(value) => field.onChange(value)}
                  label="Call Time Username"
                  placeholder="Select callname"
                  searchable
                  clearable={false}
                  allowDeselect={false}
                  withinPortal={true}
                  maxDropdownHeight={220}
                  transitionProps={{ duration: 0 }}
                  nothingFound="No callnames found"
                />
              )}
            />
          ) : (
            <MuiTextInput
              name="callTimeUserName"
              withAsterisk
              label="Call Time Username"
              placeholder="Enter Call Time Username"
              readOnly={isCastRole && !!selectedCharacter}
              {...register("callTimeUserName")}
            />
          )}
          {errors.callTimeUserName && (
            <ErrorText message={errors.callTimeUserName?.message} />
          )}

          <ManGroup position="right" mt="md">
            <ManButton disabled={isSubmitting}
            color="cyan" type="submit">
              Submit
            </ManButton>
          </ManGroup>
        </form>
      </ManBox>
    </ManModal>
  )
}
export default AddUserModal
